import { z } from 'zod';

/**
 * Per-org plugin settings.
 *
 * The per-org sibling of the deployment-global service config in
 * `plugin-service-config.types.ts`. A plugin declares an `orgSettings`
 * contract (Zod schema + defaults + optional validate hook) on its server
 * definition; the host stores the org-chosen values on the plugin's
 * `org_plugin_installations` row and never interprets them.
 *
 * Values are plugin-semantic and readable by any org member holding
 * `plugins:read`, so they must never contain secrets.
 */

/**
 * Settings object for one plugin in one org. The host treats the values
 * as opaque; the plugin's declared Zod schema is the real contract.
 */
export const OrgPluginSettingsValuesSchema = z.record(z.string(), z.unknown());
export type OrgPluginSettingsValues = z.infer<typeof OrgPluginSettingsValuesSchema>;

/**
 * View returned by `GET /organizations/:id/plugins/:name/settings`:
 * the plugin's declared defaults merged with the org's stored values.
 */
export const OrgPluginSettingsViewSchema = z.object({
  pluginName: z.string(),
  values: OrgPluginSettingsValuesSchema,
});
export type OrgPluginSettingsView = z.infer<typeof OrgPluginSettingsViewSchema>;

/** Body for `PUT /organizations/:id/plugins/:name/settings`. */
export const UpdateOrgPluginSettingsRequestSchema = z.object({
  values: OrgPluginSettingsValuesSchema,
});
export type UpdateOrgPluginSettingsRequest = z.infer<typeof UpdateOrgPluginSettingsRequestSchema>;
